import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlarmClock, CalendarCheck2, CalendarClock, CalendarDays, Download, Filter,
  Plus, Search, Users, X, CheckCircle2, KanbanSquare, Upload, UserCog, ListFilter, CalendarPlus,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { downloadCSV, fdate, money, relative } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState, LoadingRows } from "@/components/common/EmptyState";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LeadForm } from "./LeadForm";
import { RecordActivityDialog } from "./RecordActivityDialog";
import { LeadDetail } from "./LeadDetail";
import { PipelineBoard } from "./PipelineBoard";
import { ImportLeadsDialog } from "./ImportLeadsDialog";
import { cn } from "@/lib/utils";

const STATUSES = ["New Inquiry", "Contacted", "Follow-up Required", "Interested", "Quotation Created",
  "Negotiation", "Proforma Invoice Created", "Invoice Created", "Converted", "Lost", "On Hold"];
const SOURCES = ["Website", "IndiaMART", "Referral", "Exhibition", "Cold Call", "Amazon", "Walk-in", "Other"];

const BUCKETS = [
  { key: "today", label: "Today", icon: CalendarClock, tone: "warning" },
  { key: "overdue", label: "Overdue", icon: AlarmClock, tone: "danger" },
  { key: "upcoming", label: "Upcoming", icon: CalendarDays, tone: "info" },
  { key: "completed", label: "Completed", icon: CalendarCheck2, tone: "success" },
];

const TONE_BG = {
  warning: "bg-warning-soft text-warning border-warning/30",
  danger: "bg-destructive-soft text-destructive border-destructive/25",
  info: "bg-info-soft text-info border-info/25",
  success: "bg-success-soft text-success border-success/25",
};

export default function CRM() {
  const { can, isAdmin, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "leads");

  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", status: "all", assigned_to: "all", source: "all" });
  const [showFilters, setShowFilters] = useState(false);
  const [staff, setStaff] = useState([]);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(params.get("open") || null);

  const [bucket, setBucket] = useState(params.get("bucket") || "today");
  const [followups, setFollowups] = useState([]);
  const [summary, setSummary] = useState({});

  const [selected, setSelected] = useState([]);
  const [activityLead, setActivityLead] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads", { params: { ...filters, limit: 100 } });
      setLeads(data.items);
      setTotal(data.total);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [filters]);

  const loadFollowups = useCallback(async () => {
    const [f, s] = await Promise.all([
      api.get("/followups", { params: { bucket } }),
      api.get("/followups/summary"),
    ]);
    setFollowups(f.data);
    setSummary(s.data);
  }, [bucket]);

  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => { if (tab === "followups") loadFollowups(); }, [tab, loadFollowups]);
  useEffect(() => { if (isAdmin) api.get("/staff/assignable").then((r) => setStaff(r.data)).catch(() => {}); }, [isAdmin]);
  useEffect(() => {
    if (params.get("new") && can("crm", "add")) { setEditing(null); setFormOpen(true); params.delete("new"); setParams(params, { replace: true }); }
    // eslint-disable-next-line
  }, [params]);

  const activeFilters = Object.entries(filters).filter(([k, v]) => v && v !== "all" && k !== "q").length;

  const exportCSV = () => {
    downloadCSV(leads.map((l) => ({
      "Lead ID": l.lead_no, Customer: l.customer_name, Company: l.company, Phone: l.phone,
      Email: l.email, City: l.city, Product: l.product, Source: l.source, Status: l.status,
      "Assigned To": l.assigned_to_name, "Created By": l.created_by_name, Created: fdate(l.created_at),
    })), "leads.csv");
    toast.success("Leads exported to CSV");
  };

  const completeFollowup = async (f) => {
    try {
      await api.patch(`/followups/${f.id}`, { status: "Completed" });
      toast.success("Marked as completed");
      loadFollowups();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const columns = useMemo(() => [
    ...(can("crm", "edit") ? [{
      key: "select", label: "",
      className: "w-10", hideMobile: true,
      render: (r) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selected.includes(r.id)}
            onCheckedChange={() => setSelected((s) => s.includes(r.id) ? s.filter((x) => x !== r.id) : [...s, r.id])}
            data-testid={`select-${r.lead_no}`}
          />
        </div>
      ),
    }] : []),
    {
      key: "customer", label: "Customer",
      render: (r) => (
        <div className="flex items-center gap-3">
          <UserAvatar name={r.customer_name} size="sm" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{r.customer_name}</p>
            <p className="truncate text-xs text-muted-foreground">{r.company || r.phone}</p>
          </div>
        </div>
      ),
    },
    { key: "lead_no", label: "Lead ID", render: (r) => <span className="num text-xs text-muted-foreground">{r.lead_no}</span>, hideMobile: true },
    { key: "product", label: "Product", render: (r) => <span className="text-sm">{r.product || "\u2014"}</span> },
    { key: "source", label: "Source", render: (r) => <span className="text-sm text-muted-foreground">{r.source}</span>, hideMobile: true },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    ...(isAdmin ? [{ key: "assigned", label: "Owner", render: (r) => <span className="text-sm">{r.assigned_to_name || "Unassigned"}</span> }] : []),
    { key: "value", label: "Value", align: "right", render: (r) => <span className="num text-sm font-semibold">{money(r.value)}</span> },
    { key: "created", label: "Created", align: "right", render: (r) => <span className="text-xs text-muted-foreground">{relative(r.created_at)}</span>, hideMobile: true },
    ...(can("crm", "edit") ? [{
      key: "activity", label: "", align: "right",
      render: (r) => (
        <Button size="sm" variant="soft" data-testid={`record-activity-${r.lead_no}`}
          onClick={(e) => { e.stopPropagation(); setActivityLead(r); }}>
          <CalendarPlus className="h-3.5 w-3.5" /> Record activity
        </Button>
      ),
    }] : []),
  ], [isAdmin, selected, can]);

  const bulkApply = async (patch) => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/leads/bulk", { ids: selected, ...patch });
      toast.success(`${data.updated} lead${data.updated > 1 ? "s" : ""} updated`);
      setSelected([]);
      loadLeads();
    } catch (e) { toast.error(errMsg(e)); } finally { setBulkBusy(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Users}
        title="CRM"
        subtitle={user?.role === "telecaller"
          ? "Add enquiries and hand them to the right salesperson."
          : "Your leads, conversations and reminders in one place."}
        actions={
          <>
            {can("crm", "add") && (
              <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-leads-btn">
                <Upload className="h-4 w-4" /> Import
              </Button>
            )}
            {can("crm", "export") && leads.length > 0 && (
              <Button variant="outline" onClick={exportCSV}><Download className="h-4 w-4" /> Export</Button>
            )}
            {can("crm", "add") && (
              <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="new-lead-btn">
                <Plus className="h-4 w-4" /> New Lead
              </Button>
            )}
          </>
        }
      />

      <Tabs value={tab} onValueChange={(v) => { setTab(v); params.set("tab", v); setParams(params, { replace: true }); }}>
        <TabsList>
          <TabsTrigger value="leads" data-testid="tab-leads"><ListFilter className="mr-1.5 h-3.5 w-3.5" />Leads {total > 0 && <span className="num ml-1.5 text-xs opacity-70">{total}</span>}</TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="tab-pipeline"><KanbanSquare className="mr-1.5 h-3.5 w-3.5" />Pipeline</TabsTrigger>
          <TabsTrigger value="followups" data-testid="tab-followups"><CalendarClock className="mr-1.5 h-3.5 w-3.5" />Follow-ups</TabsTrigger>
        </TabsList>

        {/* LEADS */}
        <TabsContent value="leads" className="mt-5 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="lead-search"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="Search by name, company, phone, email or lead ID"
                className="pl-9"
              />
            </div>
            <Button variant={showFilters ? "soft" : "outline"} onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters">
              <Filter className="h-4 w-4" /> Filters
              {activeFilters > 0 && <span className="ml-1 rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">{activeFilters}</span>}
            </Button>
          </div>

          {showFilters && (
            <div className="grid animate-fade-up gap-3 rounded-xl border border-border bg-card p-4 sm:grid-cols-3">
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.source} onValueChange={(v) => setFilters({ ...filters, source: v })}>
                <SelectTrigger><SelectValue placeholder="Source" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              {isAdmin && (
                <Select value={filters.assigned_to} onValueChange={(v) => setFilters({ ...filters, assigned_to: v })}>
                  <SelectTrigger><SelectValue placeholder="Salesperson" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All salespeople</SelectItem>
                    {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {activeFilters > 0 && (
                <Button variant="ghost" className="sm:col-span-3 justify-start text-muted-foreground"
                  onClick={() => setFilters({ q: filters.q, status: "all", assigned_to: "all", source: "all" })}>
                  <X className="h-4 w-4" /> Clear all filters
                </Button>
              )}
            </div>
          )}

          {loading ? <LoadingRows /> : (
            <DataTable
              columns={columns}
              rows={leads}
              onRowClick={(r) => setDetailId(r.id)}
              empty={
                <EmptyState
                  icon={Users}
                  title={filters.q || activeFilters ? "No leads match your search" : "No leads yet"}
                  description={filters.q || activeFilters
                    ? "Try changing the search text or clearing the filters."
                    : "Add your first enquiry and start tracking it through to invoice."}
                  action={can("crm", "add") && <Button onClick={() => { setEditing(null); setFormOpen(true); }}><Plus className="h-4 w-4" /> New Lead</Button>}
                />
              }
            />
          )}
        </TabsContent>

        {/* PIPELINE */}
        <TabsContent value="pipeline" className="mt-5 space-y-4">
          <p className="text-sm text-muted-foreground">
            {can("crm", "edit")
              ? "Drag a card into another column to move the lead to that stage."
              : "A read-only view of where every lead stands."}
          </p>
          {loading ? <LoadingRows rows={3} /> : leads.length === 0 ? (
            <EmptyState icon={KanbanSquare} title="No leads to show" description="Create a lead and it will appear on the board." />
          ) : (
            <PipelineBoard leads={leads} onOpen={setDetailId} onChanged={loadLeads} />
          )}
        </TabsContent>

        {/* FOLLOW-UPS */}
        <TabsContent value="followups" className="mt-5 space-y-5">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {BUCKETS.map((b) => (
              <button
                key={b.key}
                onClick={() => setBucket(b.key)}
                data-testid={`bucket-${b.key}`}
                className={cn(
                  "flex items-center gap-3 rounded-xl border p-4 text-left transition-all duration-200 hover:-translate-y-0.5",
                  bucket === b.key ? `${TONE_BG[b.tone]} shadow-sm` : "border-border bg-card hover:border-primary/30"
                )}
              >
                <b.icon className={cn("h-5 w-5", bucket === b.key ? "" : "text-muted-foreground")} />
                <span className="flex-1">
                  <span className="block text-xs font-medium uppercase tracking-wide opacity-80">{b.label}</span>
                  <span className="num block text-xl font-bold">{summary[b.key] ?? 0}</span>
                </span>
              </button>
            ))}
          </div>

          {followups.length === 0 ? (
            <EmptyState icon={CalendarCheck2} title={`No ${bucket} follow-ups`}
              description="When you schedule follow-ups from a lead, they appear here." />
          ) : (
            <div className="space-y-3">
              {followups.map((f) => (
                <div key={f.id} className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center">
                  <button onClick={() => setDetailId(f.lead_id)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                    <UserAvatar name={f.customer_name} size="sm" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">{f.customer_name}</p>
                      <p className="truncate text-xs text-muted-foreground">{f.company} . {f.lead_no}</p>
                    </div>
                  </button>
                  <p className="min-w-0 flex-1 truncate text-sm text-muted-foreground">{f.remarks}</p>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs font-semibold">{fdate(f.date)} . {f.time}</p>
                      <p className="text-[11px] text-muted-foreground">{f.next_action || "\u2014"}</p>
                    </div>
                    <StatusBadge status={f.status} />
                    {f.status !== "Completed" && can("crm", "edit") && (
                      <Button size="iconSm" variant="outline" onClick={() => completeFollowup(f)} title="Mark completed">
                        <CheckCircle2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <LeadForm open={formOpen} onOpenChange={setFormOpen} lead={editing} onSaved={loadLeads} />
      {activityLead && (
        <RecordActivityDialog lead={activityLead} open={!!activityLead}
          onOpenChange={(v) => !v && setActivityLead(null)}
          onSaved={() => { loadLeads(); loadFollowups(); }} />
      )}
      <ImportLeadsDialog open={importOpen} onOpenChange={setImportOpen} onImported={loadLeads} />

      {/* Bulk action bar */}
      {selected.length > 0 && tab === "leads" && (
        <div className="fixed bottom-4 left-1/2 z-40 w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2 animate-fade-up">
          <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-3 shadow-lg sm:flex-row sm:items-center">
            <p className="flex items-center gap-2 text-sm font-semibold">
              <span className="num grid h-6 min-w-6 place-items-center rounded-full bg-primary px-1.5 text-xs text-primary-foreground">{selected.length}</span>
              selected
            </p>
            <div className="flex flex-1 flex-wrap items-center gap-2">
              <Select onValueChange={(v) => bulkApply({ status: v })} disabled={bulkBusy}>
                <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="bulk-status"><SelectValue placeholder="Change status" /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              {isAdmin && (
                <Select onValueChange={(v) => bulkApply({ assigned_to: v })} disabled={bulkBusy}>
                  <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="bulk-assign">
                    <SelectValue placeholder="Reassign to" />
                  </SelectTrigger>
                  <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
              <Button size="sm" variant="outline" onClick={() => {
                const rows = leads.filter((l) => selected.includes(l.id));
                downloadCSV(rows.map((l) => ({
                  "Lead ID": l.lead_no, Customer: l.customer_name, Company: l.company,
                  Phone: l.phone, Email: l.email, Status: l.status, Owner: l.assigned_to_name,
                })), "selected-leads.csv");
                toast.success("Selected leads exported");
              }}><Download className="h-3.5 w-3.5" /> Export</Button>
            </div>
            <Button size="iconSm" variant="ghost" onClick={() => setSelected([])}><X className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      <LeadDetail
        leadId={detailId}
        open={!!detailId}
        onOpenChange={(v) => !v && setDetailId(null)}
        onChanged={() => { loadLeads(); if (tab === "followups") loadFollowups(); }}
        onEdit={(l) => { setEditing(l); setDetailId(null); setFormOpen(true); }}
      />
    </div>
  );
}
